import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer } from 'preact-i18n';
import Markdown from 'preact-markdown';
import { route } from 'preact-router';

import ImageScroller from '../../components/imagescroller';
import VideoStreamingPlayer from '../../routes/videostreaming/player';
import Comments from '../../components/comments';
import Likes from '../../components/likes';
import TranslationButtons from '../../components/translation/buttons';
import MarkdownDisplay from '../../components/gui/markdownDisplay';

import NewsList from './list';

import localUtil from '../../lib/util';

const ICON_SIZE = '35px';

function partImg(img, props, size = '150x') {
    if (typeof img !== 'object') {
        return undefined;
    }
    const imgSrc = `${img.s3LargeLink}`;
    return imgSrc;
}

@observer
class NewsView extends Component {
  	constructor(props) {
        super(props);
        this.state = {
            sessionid: new Date().getTime(),
            activeTranslation: null,
            translationLoading: false,
            loadingLanguage: null,
        };
        this.mainContainer = null;
    }

    loadAll = async (props = this.props) => {
        const { id } = props;
        const { newsStore, userStore, appState } = this.props.stores;
        const { user } = userStore;
        const { isAdmin, isRaceAdmin, isMediaWorker } =  user;
        const isWorkingWithMedia = isAdmin || isRaceAdmin || isMediaWorker;

        if (id) {
            newsStore.updateKeyValue('news', {});
            await newsStore.load(id, false, {
                addData: ['tags', 'creators', 'likes', 'comments'],
                query: { all: 1 },
                url: isWorkingWithMedia ? null : '/api/news/public/',
            });

            const { news } = newsStore;
            if (news && news.id) {
                appState.addUsageLog({
                    type: 'news_view',
                    props: {
                        newsId: news.id,
                        title: news.title,
                    },
                    timestamp: Math.floor(new Date().getTime() / 1000),
                });
            }
        }
    }

    handleTranslate = async (language) => {
        const { newsStore } = this.props.stores;
        const { news } = newsStore;
        if (!news || !news.id) return;

        this.setState({
            translationLoading: true,
            loadingLanguage: language,
        });

        try {
            const response = await util.fetchApi(`/api/news/${news.id}/translate`, { method: 'POST' }, { language });

            if (response && response.status === 200 && response.data) {
                this.setState({
                    activeTranslation: {
                        language,
                        translation: response.data.translation,
                        cached: response.data.cached,
                    },
                    translationLoading: false,
                    loadingLanguage: null,
                });
            } else {
                this.setState({
                    translationLoading: false,
                    loadingLanguage: null,
                });
            }
        } catch (error) {
            console.error('[Translation] Error:', error);
            this.setState({
                translationLoading: false,
                loadingLanguage: null,
            });
        }
    }

    clearTranslation = () => {
        this.setState({ activeTranslation: null });
    }

    viewTags = obj => {
        if (obj.tags?.length === 0 && obj.loans?.length === 0 && !obj.ownedByOther && !obj.url && !obj.parents?.length > 0 && !obj.files?.length > 0) {
            return '';
        }

        return (<>
            <div class='w-100 overflow-hidden'>
                <div class='box-line-tags d-flex flex-nowrap'>
                    {obj.files?.length > 0 && <>
                        <span class='badge text-bg-light me-1 mt-1'>
                            <i class='fa-duotone fa-file' /> <span class='font-weight-lighter'> x {obj.files.length}</span>
                        </span>
                    </>}
                    {obj.tags?.length > 0 && obj.tags.map(tag => {
                        return (<>
                            <span
                                class='badge text-bg-secondary me-1 mt-1'
                                onClick={this.toggleTagFilter}
                                data-tag={tag.name}
                            >#{tag.name}</span>
                        </>);
                    })}
                </div>
            </div>
        </>);
    }

    editNews = () => {
		const { id, drawerLevel } = this.props;
		const { appState } = this.props.stores;
		const { drawerHeightMediumLarge } = appState;
		appState.openDrawer('newsEdit', {
			height: drawerHeightMediumLarge,
			id,
		}, drawerLevel + 1);
	}

    viewInstallation = e => {
        const { id } = e.currentTarget.dataset;
        const { appState } = this.props.stores;
        appState.toggleDrawer();
        route(`/installation/${id}`);
    };

    componentDidMount() {
        this.loadAll();
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.page !== this.props.page || nextProps.artid !== this.props.artid) {
            this.loadAll(nextProps);
        }
    }

    componentWillUnmount() {

    }

    render() {
        const {
            page,
            artid,
            bib,
            showAnimations = true,
            tag = 'frontpage',
            drawerLevel,
        } = this.props;
        const { sessionid, showMenu, activeTranslation, translationLoading, loadingLanguage } = this.state;
        const { appState, userStore, newsStore } = this.props.stores;
        const { mainView, subView, isDevelopment, path } = appState;
        const { user = {}, language } = userStore;
        const { isAdmin } = userStore;
        const isRaceAdmin = user?.isRaceAdmin === true || user?.isRaceAdmin === 1;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');

        // Coerce to {} — a not-found/not-public single-id load sets the store
        // observable to undefined (updateItem(response.data[0]) with empty data).
        const { news: loadedNews } = newsStore;
        const news = loadedNews || {};

        // Determine display content (translated or original)
        const displayTitle = activeTranslation?.translation?.title || news?.title;
        const displayIngress = activeTranslation?.translation?.ingress || news?.ingress;
        const displayBody = activeTranslation?.translation?.body || news?.body;

        // Creator info - backend populates these flat fields via addData: ['creators']
        const creatorName = news?.creator;
        const creatorImage = news?.creatorImage;

        return (<>
            {(isAdmin || isRaceAdmin) && <div class='px-3 py-2'>
                <button
                    type='button'
                    class='btn btn-sm btn-outline-primary rounded-pill'
                    onClick={this.editNews}
                >
                    <i class='fa-duotone fa-pen me-1' /> Redigere
                </button>
            </div>}
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100 h-100' style='max-width: 960px; padding-top: 20px; padding-bottom: 100px;'>

                    {/* Single card wrapping the entire news post */}
                    <div class={`w-100 d-flex flex-column rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} overflow-hidden`} style={`border: 0.5px solid ${darkmode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.10)'};`}>

                        {/* Creator byline - ABOVE the image */}
                        {creatorName && <div class='d-flex flex-row align-items-center px-3 py-2'>
                            <span class='d-flex justify-content-center align-items-center rounded-circle bg-light text-dark me-2 border' style={`width: ${ICON_SIZE}; height: ${ICON_SIZE}; min-width: ${ICON_SIZE};`}>
                                {creatorImage ? <>
                                    <img src={creatorImage} class='rounded-circle' style={`width: ${ICON_SIZE}; height: ${ICON_SIZE};`} />
                                </> : <>
                                    <i class='fa-duotone fa-user' style='font-size: 1.5rem; line-height: 1.5rem;' />
                                </>}
                            </span>
                            <span class='text-body-secondary font-weight-lighter'>
                                {creatorName}
                                <span class='text-body-secondary px-2'>&bull;</span>
                                {localUtil.getTimeAgo(news.publishDate || news.createdDate)}
                            </span>
                        </div>}

                        {/* Video/Image - full width within the card */}
                        {news.files?.length > 0 && news.files.map(file => {
                            const poster = file.s3LinkStreamPreview1;
                            if (file.s3LinkStream) {
                                return (<>
                                    <div class='video-player-wrapper d-flex flex-column bg-dark'>
                                        <VideoStreamingPlayer
                                            key={file.s3LinkStream}
                                            videoStreamUrl={file.s3LinkStream}
                                            stores={this.props.stores}
                                            poster={poster}
                                        />
                                    </div>
                                </>);
                            }
                            if (file.s3Link.match(/\.(m4v|m4a|mp4)$/i)) {
                                return (<>
                                    <div className='video-container'>
                                        <video controls className='video-player'>
                                            <source src={file.s3Link} type='video/mp4' />
                                            Your browser does not support the video tag.
                                        </video>
                                    </div>
                                </>);
                            }
                            if (file.s3Link.match(/\.(mov)$/i)) {
                                return (<>
                                    <div className='video-container'>
                                        <video controls className='video-player'>
                                            <source src={file.s3Link} type='video/quicktime' />
                                            Your browser does not support the video tag.
                                        </video>
                                    </div>
                                    <div class='video-info d-flex flex-row justify-content-center'>
                                        <span class='text-body-secondary'>size:</span> {localUtil.formatFileSize(file.size)}
                                        <span class='text-body-secondary ms-3 me-1'>encoding:</span> {file.encoding}
                                        <span class='text-body-secondary ms-3 me-1'>mimetype:</span> {file.mimetype}
                                        <span class='text-body-secondary ms-3 me-1'>ext:</span> {file.ext}
                                        <span class='text-body-secondary ms-3 me-1'>s3Link:</span> <a href={file.s3Link} target='_blank' rel='noopener noreferrer'>download</a>
                                    </div>
                                </>);
                            }
                        })}
                        <ImageScroller
                            images={news.images}
                            stores={this.props.stores}
                            showImg={img => localUtil.displayImg(img, this.props, '1920x')}
                            maxHeight='80vh'
                            rounded={false}
                            classNamesWrapper1=''
                        />

                        {/* Content section - padded within the card */}
                        <div class='d-flex flex-column px-3 pt-3 pb-2'>
                            <div style='font-size: clamp(1.5rem, 4vw, 2rem); font-weight: 700; line-height: 1.2em;'>
                                {displayTitle}
                            </div>

                            {displayIngress && <div class='d-flex flex-column mb-3 font-weight-bold'>
                                <MarkdownDisplay markdown={localUtil.replaceImages(displayIngress, news.images, language, false)} stores={this.props.stores} />
                            </div>}
                            {displayBody && <div class='d-flex flex-column'>
                                <MarkdownDisplay markdown={localUtil.replaceImages(displayBody, news.images, language, false)} stores={this.props.stores} />
                            </div>}

                            {/* File downloads */}
                            {news.files && news.files.length > 0 && <>
                                <div class='d-flex flex-wrap mt-3'>
                                    {news.files.map(file => {
                                        return (<>
                                            <a href={file.s3Link} class='badge text-bg-light border me-2 mb-1' target='_blank' rel='noopener noreferrer'>{file.name} <span class='font-weight-lighter'>({localUtil.formatFileSize(file.size)})</span></a>
                                        </>);
                                    })}
                                </div>
                            </>}

                            {/* Tags */}
                            {news.tags && news.tags.length > 0 && <div class='mt-2'>
                                {this.viewTags(news)}
                            </div>}
                        </div>

                        {/* Likes and Comments - inside the same card */}
                        {news.id && <>
                            <Likes
                                type='news'
                                objectId={news.id}
                                likes={news.likes || []}
                                stores={this.props.stores}
                                shareTitle={news.title}
                                shareSummary={news.ingress || news.body}
                                shareImage={news.images?.[0]?.s3SmallLink}
                            >
                                <TranslationButtons
                                    objectId={news.id}
                                    objectType='news'
                                    translations={news.translations || {}}
                                    activeLanguage={activeTranslation?.language || null}
                                    onTranslate={this.handleTranslate}
                                    onClearTranslation={this.clearTranslation}
                                    loading={translationLoading}
                                    loadingLanguage={loadingLanguage}
                                />
                            </Likes>
                            <Comments
                                type='news'
                                objectId={news.id}
                                comments={news.comments || []}
                                stores={this.props.stores}
                            />
                        </>}

                    </div>

                    {/* More news list */}
                    {news.id && (
                        <NewsList
                            classNames={'mt-5'}
                            key={`more-news-list`}
                            keyName={`more-news-list`}
                            stores={this.props.stores}
                            hideEmpty={true}
                            hideAdminButtons={true}
                            limit={10}
                            tag={tag}
                            raceId={news.race}
                            skipUpdate={true}
                            showHeader={true}
                            title={<Text id='news.list-more'>More news</Text>}
                            drawerLevel={drawerLevel - 1}
                        />
                    )}

                </div>
            </div>
        </>);
    }
}

export default NewsView;
